"use client";

import { useEffect, useState } from "react";
import DatasetDS from "../../DataServices/FilesUploadDs";
import { useCustomContext } from "../CustomComponents/CustomComponents";
import { PDFDocument } from "pdf-lib";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { insertLog } from "../Logs/InsertLog";
const FileUploadPageLogic = () => {

    const datasetDS = new DatasetDS();
    const { hud, stopHudRotation, showToast, showAlert } = useCustomContext();
    const [datasets, setDatasets] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDataset, setSelectedDataset] = useState(null);
    const [vinId, setVinId] = useState("");
    const [notes, setNotes] = useState("");
    const [files, setFiles] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [errors, setErrors] = useState({});
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(Date.now());
    const useremail = localStorage.getItem("email");
    const userName = useremail?.split("@")[0];

    const normalizeText = (value) => {
        return value.replace(/\s+/g, " ").trim();
    };
    useEffect(() => {
        fetchDatasets();
    }, []);

    const fetchDatasets = async () => {
        try {

            hud("Please wait...");

            const response =
                await datasetDS.AP_Dataset_List_GET({});

            const data = response.data;

            const body =
                typeof data.body === "string"
                    ? JSON.parse(data.body)
                    : data.body;

            if (
                data.statusCode === 200 &&
                Array.isArray(body.records)
            ) {

                const requestDatasets =
                    body.records.filter(
                        item =>
                            item.entryType === "Request Based PDF Entry"
                    );

                setDatasets(requestDatasets);
            } else {
                insertLog(
                    `${userName} failed to fetch datasets`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    '',
                    'GET',
                    'AP_Dataset_List_GET',
                    'FAILED',
                    'Unable to load datasets'
                );
                showAlert(
                    body?.message || "Failed to load datasets",
                    [
                        {
                            label: "OK",
                            onClick: () => { },
                            color: "red"
                        }
                    ]
                );
            }
        } catch (error) {
            insertLog(
                `${userName} failed to fetch datasets`,
                0,
                useremail,
                hud,
                stopHudRotation,
                showAlert,
                '',
                'GET',
                'AP_Dataset_List_GET',
                'FAILED',
                error.message
            );
            console.log(error);
            showAlert(
                error.message || "Error fetching datasets",
                [
                    {
                        label: "OK",
                        onClick: () => { },
                        color: "red"
                    }
                ]
            );
        } finally {

            stopHudRotation();
        }
    };

    const handleOpenModal = (dataset) => {
        setSelectedDataset(dataset);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setVinId("");
        setNotes("");
        setFiles([]);
        setTotalPages(0);
        setErrors({});
        setFileInputKey(Date.now());
    };

    const handleVinIdChange = (e) => {
        const value = e.target.value.replace(/^\s+/, "");
        setVinId(value);
        if (errors.vinId) {
            setErrors(prev => ({
                ...prev,
                vinId: ""
            }));
        }
    };
    const vinRegex = /^VIN-.+-\d{4}-\d{2}-\d{2}-\d{3,5}$/;

    const handleVinIdBlur = () => {
        if (!vinId.trim()) {
            setErrors(prev => ({
                ...prev,
                vinId: "VIN ID is required."
            }));
        } else if (!vinRegex.test(vinId.trim())) {
            setErrors(prev => ({
                ...prev,
                vinId:
                    "Invalid VIN ID format."
            }));
        } else {
            setErrors(prev => ({
                ...prev,
                vinId: ""
            }));
        }
    };

    const handleNotesChange = (e) => {
        const value = e.target.value.replace(/^\s+/, "");
        setNotes(value);
        if (errors.notes) {
            setErrors(prev => ({
                ...prev,
                notes: ""
            }));
        }
    };

    const renameFileWithTimestamp = (file) => {
        const timestamp = Date.now();
        const newName = `${timestamp}.pdf`;
        return new File([file], newName, { type: file.type });
    };

    const handleFileChange = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        // ONLY PDF VALIDATION
        const invalidFiles =
            selectedFiles.filter(
                file => file.type !== "application/pdf"
            );

        if (invalidFiles.length > 0) {

            setErrors(prev => ({
                ...prev,
                files: "Only PDF files are allowed"
            }));

            e.target.value = "";

            return;
        }

        // DUPLICATE FILE CHECK (by original name against already uploaded files)
        const existingNames = files.map(f => f.originalName);

        const duplicates = selectedFiles.filter(
            file => existingNames.includes(file.name)
        );

        if (duplicates.length > 0) {

            showAlert(
                `File "${duplicates[0].name}" is already uploaded`,
                [
                    {
                        label: "OK",
                        onClick: () => { },
                        color: "var(--buttonColor)"
                    }
                ]
            );
        }

        const totalFileCount =
            files.length + selectedFiles.length;
        if (totalFileCount > 50) {
            setErrors(prev => ({
                ...prev,
                files: "Maximum 50 files allowed"
            }));

            return;
        }

        const totalSize =
            [...files, ...selectedFiles]
                .reduce((acc, file) => acc + file.size, 0);

        if (totalSize > 1024 * 1024 * 1024) {
            setErrors(prev => ({
                ...prev,
                files: "Maximum upload size is 1GB"
            }));

            return;
        }

        // RENAME each file with timestamp and keep original name
        const renamedFiles = selectedFiles.map(file => {
            const renamedFile = renameFileWithTimestamp(file);
            // Attach original name as custom property
            renamedFile.originalName = file.name;

            return renamedFile;
        });

        // PAGE COUNT — only count newly selected files
        let newPagesCount = 0;

        for (const file of renamedFiles) {
            const arrayBuffer = await file.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            const str = new TextDecoder("latin1").decode(bytes);
            const matches = str.match(/\/Type\s*\/Page[^s]/g);
            newPagesCount += matches ? matches.length : 0;
        }

        // ACCUMULATE pages and files (append, don't replace)
        setTotalPages(prev => prev + newPagesCount);
        setFiles(prev => [...prev, ...renamedFiles]);
        // Reset file input so same file can be re-selected
        setFileInputKey(Date.now());
        setErrors(prev => ({
            ...prev,
            files: ""
        }));
    };

    const removeFile = async (index) => {
        const fileToRemove = files[index];
        let removedPages = 0;
        if (fileToRemove) {
            const arrayBuffer =
                await fileToRemove.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            const str = new TextDecoder("latin1").decode(bytes);
            const matches = str.match(/\/Type\s*\/Page[^s]/g);
            removedPages = matches ? matches.length : 0;
        }

        const updatedFiles =
            files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        setTotalPages(prev => Math.max(0, prev - removedPages));
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDropOnFolder = (e, dataset) => {
        e.preventDefault();
        setDragOverIndex(null);
        const droppedFiles =
            Array.from(e.dataTransfer.files);
        if (droppedFiles.length === 0) return;
        // ONLY PDF VALIDATION
        const invalidFiles =
            droppedFiles.filter(
                file => file.type !== "application/pdf"
            );
        if (invalidFiles.length > 0) {

            setErrors(prev => ({
                ...prev,
                files: "Only PDF files are allowed"
            }));

            return;
        }

        handleOpenModal(dataset);
        setTimeout(async () => {
            let pagesCount = 0;
            for (const file of droppedFiles) {
                const arrayBuffer = await file.arrayBuffer();
                const bytes = new Uint8Array(arrayBuffer);
                const str = new TextDecoder("latin1").decode(bytes);
                const matches = str.match(/\/Type\s*\/Page[^s]/g);
                pagesCount += matches ? matches.length : 0;
            }
            setTotalPages(pagesCount);
            setFiles(droppedFiles);

        }, 100);
    };

    const validateFields = () => {
        let newErrors = {};
        if (!normalizeText(vinId)) {
            newErrors.vinId = "Vin Id is required";
        }
        if (!normalizeText(notes)) {
            newErrors.notes = "Notes are required";
        }
        if (files.length === 0) {
            newErrors.files = "Choose at least one file";
        }
        setErrors(newErrors);
        return newErrors;
    };

    const uploadToS3 = async (mergedPdfBytes, mergedFileName, mergedS3Key, bucketName) => {
        const idToken = localStorage.getItem("idToken");
        const s3 = new S3Client({
            region: process.env.REACT_APP_AWS_REGION,
            credentials: fromCognitoIdentityPool({
                clientConfig: {
                    region: process.env.REACT_APP_AWS_REGION
                },
                identityPoolId:
                    process.env.REACT_APP_CONNECT_IDENTITY_POOL_ID,
                logins: {
                    [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${process.env.REACT_APP_COGNITO_USERPOOLID}`]:
                        idToken,
                },
            }),
        });

        const command = new PutObjectCommand({
            Bucket: bucketName,
            Key: mergedS3Key,
            Body: mergedPdfBytes,
            ContentType: "application/pdf",
        });
        await s3.send(command);
        console.log("S3 Upload Success =>", mergedS3Key);
        return mergedS3Key;
    };

    const mergePdfFiles = async (pdfFiles) => {
        const mergedPdf = await PDFDocument.create();
        for (const file of pdfFiles) {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await PDFDocument.load(arrayBuffer);
            const copiedPages =
                await mergedPdf.copyPages(
                    pdf,
                    pdf.getPageIndices()
                );
            copiedPages.forEach(page => {
                mergedPdf.addPage(page);
            });
        }

        return await mergedPdf.save();
    };

    const handleUpload = async () => {
        const validationErrors = validateFields();
        if (Object.keys(validationErrors).length > 0) {
            return;
        }
        try {
            hud("Uploading files...");
            const timestamp = Date.now();
            const mergedFileName = `merged_${timestamp}.pdf`;
            const datasetID = selectedDataset?.datasetID;
            const datasetName = selectedDataset?.datasetName;
            const bucketName = `${datasetID.toLowerCase()}-${datasetName.toLowerCase().replace(/\s+/g, "-")}-files-${process.env.REACT_APP_ALIAS}`;
            console.log("Selected Dataset =>", selectedDataset);
            console.log("Dataset ID =>", datasetID);
            console.log("Dataset Name =>", datasetName);
            console.log("Bucket Name =>", bucketName);
            const mergedS3Key =
                `Uploaded-Files/${normalizeText(vinId)}/${mergedFileName}`;
            // STEP 1 — MERGE PDFs
            const mergedPdfBytes = await mergePdfFiles(files);
            // STEP 2 — UPLOAD MERGED PDF TO S3
            await uploadToS3(
                mergedPdfBytes,
                mergedFileName,
                mergedS3Key,
                bucketName
            );
            // STEP 3 — DB PAYLOAD
            const payload = {
                request_id: normalizeText(vinId),
                datasetID: datasetID,
                datasetName: datasetName,
                source: "File-Uploader",
                entry_status: "ENTRY NOT YET STARTED",
                notes: [
                    {
                        text: normalizeText(notes),
                        addedBy: useremail,
                        addedAt: new Date().toISOString()
                    }
                ],
                uploaded_time: new Date().toISOString(),
                s3_key: mergedS3Key,
                file_name: mergedFileName,
                mime_type: "application/pdf",
                total_files: files.length,
                pages: totalPages || 0
            };
            // STEP 4 — SAVE METADATA
            const response =
                await datasetDS.AP_Files_Upload_INSERT(payload);
            console.log("DB RESPONSE =>", response);
            console.log("UPLOAD PAYLOAD =>", payload);
            if (response?.data?.statusCode === 200) {
                insertLog(
                    `${userName} uploaded files successfully for Dataset ${selectedDataset?.datasetName}`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    selectedDataset?.datasetName || '',
                    'INSERT',
                    'AP_Files_Upload_INSERT',
                    'SUCCESS',
                    ''
                );
                showAlert(
                    "File Uploaded Successfully",
                    [
                        {
                            label: "OK",
                            onClick: () => { },
                            color: "var(--buttonColor)"
                        }
                    ]
                );
                handleCloseModal();
            } else {
                const body = JSON.parse(
                    response?.data?.body || "{}"
                );
                insertLog(
                    `${userName} failed to upload files for Dataset ${selectedDataset?.datasetName}`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    selectedDataset?.datasetName || '',
                    'INSERT',
                    'AP_Files_Upload_INSERT',
                    'FAILED',
                    'Unable to upload files'
                );
                showAlert(
                    `VIN ID not found for ${selectedDataset?.datasetName}`,
                    [
                        {
                            label: "OK",
                            onClick: () => { },
                            color: "red"
                        }
                    ]
                );
            }
        } catch (error) {
            insertLog(
                `${userName} failed to upload files for Dataset ${selectedDataset?.datasetName}`,
                0,
                useremail,
                hud,
                stopHudRotation,
                showAlert,
                selectedDataset?.datasetName || '',
                'INSERT',
                'AP_Files_Upload_INSERT',
                'FAILED',
                error.message
            );
            console.log(error);
            showAlert(
                error.message || "Failed to upload files",
                [
                    {
                        label: "OK",
                        onClick: () => { },
                        color: "red"
                    }
                ]
            );
        } finally {
            stopHudRotation();
        }
    };
    return {
        datasets,
        isModalOpen,
        selectedDataset,
        vinId,
        notes,
        files,
        totalPages,
        errors,
        fileInputKey,
        handleOpenModal,
        handleCloseModal,
        handleVinIdChange,
        handleVinIdBlur,
        handleNotesChange,
        handleFileChange,
        removeFile,
        handleUpload,
        dragOverIndex,
        handleDragOver,
        handleDragLeave,
        handleDropOnFolder
    };
};

export default FileUploadPageLogic;